import React, { useEffect, useState, useCallback, memo } from 'react';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
	useExperienceAbort,
} from '@atlassian/jira-business-experience-tracking';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { ff } from '@atlassian/jira-feature-flagging';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors';
import { useFlagService } from '@atlassian/jira-flags';
import { useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import { FETCH_RELEASE_EXPERIENCE } from '../../common/constants';
import {
	ConnectedProjectNotFoundError,
	getReleasesByProject,
} from '../../services/calendar-releases';
import { useConnectedProject, useChangeConnectedProject } from '../connected-projects';
import messages from './messages';
import type { StateType, CalendarReleasesInput, CalendarReleasesInternalProps } from './types';
import { transformData } from './utils';

const defaultCalendarReleasesState: StateType = {
	releases: [],
	isLoading: false,
	error: null,
	connectedProject: null,
};

const CalendarReleasesInternal = memo(
	({
		releaseDateBefore,
		releaseDateAfter,
		projectId,
		setCalendarReleasesData,
	}: CalendarReleasesInternalProps) => {
		const project = useProject();
		const isProjectAdmin = project.permissions.administerProject;
		const { connectedProject, error: initConnectedProjectError } = useConnectedProject(projectId);
		const { onProjectChange: disconnectProject } = useChangeConnectedProject(projectId);
		const { showFlag } = useFlagService();

		const startExperience = useExperienceStart(FETCH_RELEASE_EXPERIENCE);
		const onSuccess = useExperienceSuccess(FETCH_RELEASE_EXPERIENCE);
		const onFail = useExperienceFail(FETCH_RELEASE_EXPERIENCE);
		const onAbort = useExperienceAbort(FETCH_RELEASE_EXPERIENCE);

		useEffect(() => {
			startExperience();
		}, [startExperience]);

		useEffect(() => {
			if (initConnectedProjectError) {
				if (ff('jwm.abort-calendar-releases-experience-on-4xx')) {
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					const statusCode = (initConnectedProjectError as FetchError)?.statusCode;

					if (statusCode && (statusCode === 403 || statusCode === 429)) {
						showFlag({
							type: 'error',
							title:
								statusCode === 403
									? messages.permissionDeniedErrorTitle
									: messages.tooManyRequestsErrorTitle,
							description:
								statusCode === 403
									? messages.permissionDeniedErrorDescription
									: messages.tooManyRequestsErrorDescription,
						});
						onAbort('Cannot fetch the connected project');
					} else {
						showFlag({
							type: 'error',
							title: messages.fetchReleasesErrorTitle,
							description: messages.fetchReleasesErrorMessage,
						});
						onFail(
							'Did not fetch releases due to failing to fetch the connected project',
							initConnectedProjectError,
						);
					}
				} else {
					onFail(
						'Did not fetch releases due to failing to fetch the connected project',
						initConnectedProjectError,
					);
					showFlag({
						type: 'error',
						title: messages.fetchReleasesErrorTitle,
						description: messages.fetchReleasesErrorMessage,
					});
				}
			}
		}, [initConnectedProjectError, onFail, onAbort, showFlag]);

		const handleProjectNotFound = useCallback(() => {
			if (isProjectAdmin) {
				disconnectProject();
			}

			// No error as we've handled it, no releases or connected project at this point.
			setCalendarReleasesData(defaultCalendarReleasesState);
			showFlag({
				type: 'error',
				title: messages.connectedProjectNotFoundErrorTitle,
				description: isProjectAdmin
					? messages.connectedProjectNotFoundAndDisconnectedMessage
					: messages.connectedProjectNotFoundErrorMessage,
			});
		}, [disconnectProject, isProjectAdmin, setCalendarReleasesData, showFlag]);

		useEffect(() => {
			async function fetchData() {
				if (connectedProject) {
					try {
						setCalendarReleasesData({
							isLoading: true,
							error: null,
							releases: [],
							connectedProject,
						});
						const releasesByProject = await getReleasesByProject({
							projectAri: connectedProject.resourceId,
							releaseDateBefore,
							releaseDateAfter,
						});
						setCalendarReleasesData({
							isLoading: false,
							error: null,
							releases: transformData(releasesByProject ?? []),
							connectedProject,
						});
						onSuccess();
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (err: any) {
						// This exception is currently only be thrown in the API if the project does not exist.
						if (err instanceof ConnectedProjectNotFoundError) {
							handleProjectNotFound();
						} else {
							// Handle any other exceptions
							setCalendarReleasesData({
								isLoading: false,
								error: err,
								releases: [],
								connectedProject,
							});
							showFlag({
								type: 'error',
								title: messages.fetchReleasesErrorTitle,
								description: messages.fetchReleasesErrorMessage,
							});
							onFail('Failed to fetch releases', err);
						}
					}
				} else {
					setCalendarReleasesData(defaultCalendarReleasesState);
				}
			}
			fetchData();
		}, [
			connectedProject,
			releaseDateAfter,
			releaseDateBefore,
			setCalendarReleasesData,
			showFlag,
			onFail,
			onSuccess,
			startExperience,
			isProjectAdmin,
			handleProjectNotFound,
		]);

		return null;
	},
);

export const useCalendarReleasesInitializer = (request: CalendarReleasesInput) => {
	const [calendarReleasesData, setCalendarReleasesData] = useState(defaultCalendarReleasesState);
	const { data: entity } = useBusinessEntity();
	const { releaseDateAfter, releaseDateBefore } = request;
	const licensedProducts = useLicensedProducts();
	const hasSoftwareLicence = licensedProducts?.[LICENSED_PRODUCTS.JIRA_SOFTWARE];
	const CalendarReleasesInitializer = useCallback(
		() =>
			hasSoftwareLicence ? (
				<CalendarReleasesInternal
					projectId={entity.id}
					releaseDateAfter={releaseDateAfter}
					releaseDateBefore={releaseDateBefore}
					setCalendarReleasesData={setCalendarReleasesData}
				/>
			) : null,
		[releaseDateAfter, releaseDateBefore, entity, hasSoftwareLicence],
	);

	return {
		CalendarReleasesInitializer,
		calendarReleasesData,
		analyticsAttributes: {
			releasesCount: calendarReleasesData.releases.length ?? 0,
			hasConnectedProject: calendarReleasesData.connectedProject !== null,
		},
	};
};
